import {
	makeGetAdditionalCoverage,
	makeGetBeneficiaryDocumentUrl,
	makeGetDebtSettlementStatementUrl,
	makeGetExamResultsUrl,
	makeGetHelpDeskSacLinkPage,
	makeGetPerksClubLink,
	makeGetPinSsUrl,
	makeGetPortabilityCardUrl,
	makeGetStayLetterUrl,
} from 'main/factories/usecases';

import {
	AuthorizationsEnum,
	ConsultProtocolsEnum,
	EmergencyTelehealthEnum,
	ExamBookingEnum,
	ExamResultsEnum,
	FeaturesEnum,
	GuidesEnum,
	HealthBookingEnum,
	HelpCenterEnum,
	MyAppointmentsEnum,
	MyHospitalizationFlowEnum,
	MyPaymentsEnum,
	MyReferralsEnum,
	OdontoBookingEnum,
	PerksEnum,
	RefundEnum,
	TelehealthBookingEnum,
	TermsOriginEnum,
	TokenEnum,
} from 'presentation/constants';
import {ChangeDueDateEnum} from 'modules/financial/routes/mapRoutesFinancial';
import {useContractStatus, useHealthEmergencyLinkFactory, useHostName} from 'presentation/hooks';

import {
	AppointmentBookingEnum,
	AutoSolEnum,
	ExternalServicesTaggingEnum,
	MyReferralsTaggingEnum,
	RefundTaggingEnum,
	ServicesTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {makeGetWhoIndicatesLink} from 'main/factories/usecases/private/remote-home/get-who-indicates';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {
	CategoriesEnum,
	ExternalServicesTitlesEnum,
	InvoicesNFLinksEnum,
	Service,
	ServiceList,
	ServicesCenterTitlesEnum,
	ServicesTitlesEnum,
} from '../useServicesList/types';

import {
	FINANCIAL_SUPPORT_WHATSAPP_LINK,
	FINANCIAL_SUPPORT_WHATSAPP_LINK_PPO,
} from 'presentation/pages/private/help/constants/channelsContacts';
import {useAccountInformation} from '../useAccountInformation';
import {ServicesTypesEnum} from '../useServicesListForShortcuts/types';
import {findItemByQuery} from '../utils/findItemByQuery';
import {useVoiceTranscriptionDrawerStore} from 'presentation/components/VoiceTranscriptionDrawer/store';
import {useConciergeBenefits} from '../useConciergeBenefits';
import {useNavigate} from 'react-router-dom';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {useAdvanceDiagnosticsBookingStore} from 'presentation/pages/private/store/useAdvanceDiagnosticsBookingStore';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {EXAMS_FEATURE_BY_HOST} from 'presentation/constants/examsFeatureByHost';
import saludiaLogo from 'presentation/assets/images/saludia-logo.svg';
import {useSaludiaExperience} from 'presentation/hooks/useSaludiaExperience';
import {useSaludiaDrawerStore} from 'presentation/pages/private/store/useSaludiaDrawerStore';

const NOTREDAME_REFUND_ODONTO_URL =
	'https://sigo.sh.srv.br/pls/webndi/webNewDentalCliente.pr_login_cliente_opmenu?pOrgAmb=2&pOpMenu=13';

const NOTREDAME_SERVICE_CENTER_URL = 'https://cafex.gndi.com.br/sigo/linha-frente/#/login/14';

const NOTREDAME_PORTABILITY_URL = 'https://cafex.gndi.com.br/sigo/portabilidade/#/home/14';

export function useNotredameServicesList(query = '') {
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();
	const {isNotredameSaude, dataHostNameState} = useHostName();

	const {
		flPlanoSomenteHospitalar,
		businessPlan,
		healthOnly,
		odontoOnly,
		healthOdonto,
		flAdministradora,
		flPad,
		accesses,
		isResponsibleWithoutBenefits,
		isB2BAccess,
		isHolder,
		isEarlyAccess,
		isPPOPlan,
	} = useAccountInformation();
	const financialSupportWhatsappLink = isPPOPlan
		? FINANCIAL_SUPPORT_WHATSAPP_LINK_PPO
		: FINANCIAL_SUPPORT_WHATSAPP_LINK;
	const {
		isValid,
		isContractSuspended,
		isCancelledAtOneYearAndThreeMonths,
		isCancelledLessThanThreeMonths,
		isCancelledAtTwoYears,
		isCancelledAtFiveYears,
	} = useContractStatus();
	const navigate = useNavigate();

	const onOpenVoiceTranscriptionDrawer = useVoiceTranscriptionDrawerStore(state => state.onOpen);
	const setPpoNetworkType = usePpoNetworkStore(state => state.setPpoNetworkType);
	const openSaludiaDrawer = useSaludiaDrawerStore(state => state.openSaludiaDrawer);
	const {isSaludiaEnabled} = useSaludiaExperience();
	const startAdvanceDiagnosticsBooking = useAdvanceDiagnosticsBookingStore(
		state => state.startAdvanceDiagnosticsBooking,
	);

	const {isEnabled: isIrDemonstrativeFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_INCOME_TAX,
	);

	const {isEnabled: isMyReferralsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_MEUS_ENCAMINHAMENTOS,
	);

	const {isEnabled: isLandingPageTeleEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_LANDING_PAGE_TELE,
	);

	const {isEnabled: isUrgencyTeleTermsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_URGENCY_TELE_TERMS,
	);

	const {isEnabled: isAuthorizationOdontoFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_623_ODONTO,
	);

	const {isEnabled: isShowOmbudsmanInChannelsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SHOW_OMBUDSMAN_IN_CHANNELS,
	);

	const {isEnabled: isLifePlaceEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_LIFEPLACE);

	const {isEnabled: isAdvanceDiagnosticsExamsJourneyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PPO_ADVANCE_DIAGNOSTICS_EXAMS_JOURNEY,
	);

	const {isEnabled: isAdmChangeDueDateEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADM_CHANGE_DUE_DATE,
	);

	const {isEnabled: isHidePaymentSupportEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_HIDE_PAYMENT_SUPPORT_ICON,
	);

	const showHealthBookService =
		!isResponsibleWithoutBenefits &&
		!isContractSuspended &&
		((healthOnly && !flPlanoSomenteHospitalar) || (healthOdonto && !flPlanoSomenteHospitalar));

	const showBookService =
		!isResponsibleWithoutBenefits &&
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar));

	const showOdontoBookService =
		!isResponsibleWithoutBenefits && !isContractSuspended && (odontoOnly || healthOdonto);

	const showBookHealth = isValid && showHealthBookService;
	const showBookExams = isValid && !isContractSuspended && showBookService;
	const showBookOdonto = isValid && showOdontoBookService;
	const showMyReferrals =
		isValid &&
		!isContractSuspended &&
		!isResponsibleWithoutBenefits &&
		isMyReferralsFeatureFlagEnabled &&
		(healthOnly || healthOdonto);

	const showEmergencyTelehealth = isValid && showBookService && !isB2BAccess;
	const showBookTelehealth = isValid && !isContractSuspended && showBookService;

	const {showConciergeButton, onOpenConciergeDrawer} = useConciergeBenefits();
	const {isEnabled: isServiceNfEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_SERVICE_NF);
	const {isEnabled: isHideRecommendationEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_HIDE_RECOMMENDATION,
	);

	const isIndividualPlan = flPad || (!businessPlan && !flAdministradora);
	const showInvoicesOption =
		isIndividualPlan && !isResponsibleWithoutBenefits && isServiceNfEnabled;
	const showServiceCenterLinks = isIndividualPlan && isValid;

	const showRefundService = isValid && accesses?.refund && !odontoOnly && isNotredameSaude;
	let emergencyTelehealthService: {
		route?: EmergencyTelehealthEnum;
		handleClick?: () => void;
		endpoint?: typeof makeHealthEmergencyLink;
	} = {endpoint: makeHealthEmergencyLink};

	if (isLandingPageTeleEnabled) {
		emergencyTelehealthService = {route: EmergencyTelehealthEnum.EMERGENCY_TELEHEALTH_FLOW};
	} else if (isUrgencyTeleTermsEnabled) {
		emergencyTelehealthService = {
			handleClick: () =>
				onOpenVoiceTranscriptionDrawer(null, TermsOriginEnum.EMERGENCY_TELEHEALTH),
		};
	}

	const showRefund =
		(isCancelledAtOneYearAndThreeMonths &&
			accesses?.refund &&
			!odontoOnly &&
			isNotredameSaude) ||
		showRefundService;

	const showHospitalizationService =
		(healthOdonto || (healthOnly && !flPlanoSomenteHospitalar)) &&
		isValid &&
		!isResponsibleWithoutBenefits;

	const showExamsResults =
		!isResponsibleWithoutBenefits &&
		(isValid || isCancelledAtTwoYears) &&
		!odontoOnly &&
		!isB2BAccess &&
		!isEarlyAccess;

	const showMyAppointments =
		(isValid && !isResponsibleWithoutBenefits && !(healthOnly && flPlanoSomenteHospitalar)) ||
		(isCancelledLessThanThreeMonths && !isResponsibleWithoutBenefits);

	const showHealthAuthorizationsService =
		!isResponsibleWithoutBenefits && (healthOnly || healthOdonto);

	const showOdontoAuthorizationsService =
		!isResponsibleWithoutBenefits &&
		isAuthorizationOdontoFeatureFlagEnabled &&
		(odontoOnly || healthOdonto);

	const showHealthAuthorizations = isValid && showHealthAuthorizationsService && !isEarlyAccess;

	const showOdontoAuthorizations = isValid && showOdontoAuthorizationsService && !isEarlyAccess;

	const showRefundOdonto = isNotredameSaude && !healthOnly && !isResponsibleWithoutBenefits;

	const showBenefitsForGndiSaoPaulo =
		isNotredameSaude && accesses?.hasBenefits && !isResponsibleWithoutBenefits;

	const shouldShowTab = flPad || (!businessPlan && !flAdministradora);
	const showIRBase = shouldShowTab || (businessPlan && accesses.incomeTaxEmploy);
	const showIrDemonstrative = () => {
		if (!showIRBase || isB2BAccess) return false;

		const isFeatureEnabledAndValid = isIrDemonstrativeFeatureFlagEnabled && isValid;
		const isSpecialResponsible = isResponsibleWithoutBenefits || isCancelledAtFiveYears;

		return isFeatureEnabledAndValid || isSpecialResponsible;
	};

	const showSaludiaNetwork =
		isSaludiaEnabled && isValid && !isB2BAccess && !isResponsibleWithoutBenefits;

	const getInvoicesNFLinkByProduct = (product: string): string => {
		const linkMap: Record<string, string> = {
			hap: InvoicesNFLinksEnum.HAPVIDA,
			ccg: InvoicesNFLinksEnum.CCG,
			gndiminas: InvoicesNFLinksEnum.NDIMINAS,
			clinipam: InvoicesNFLinksEnum.CLINIPAM,
			gndi: InvoicesNFLinksEnum.NDI_SP,
			notredame: InvoicesNFLinksEnum.NDI_SP,
		};

		return linkMap[product] || InvoicesNFLinksEnum.HAPVIDA;
	};

	const NOTREDAME_SERVICES_LIST: ServiceList[] = [
		{
			category: CategoriesEnum.NETWORK,
			list: [
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_HEALTH,
					icon: 'stethoscope',
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_MEDICO,
					handleClick: () => {
						setPpoNetworkType(ppoNetworkTypeEnum.ACCREDITED_NETWORK);
						navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);
					},
				},
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_ODONTO,
					icon: 'tooth-outline',
					route: GuidesEnum.SERVICE_NETWORK_ODONTO,
					show: !isResponsibleWithoutBenefits && !healthOnly,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_ODONTO,
				},
				{
					title: ServicesTitlesEnum.SALUDIA_NETWORK,
					imageSrc: saludiaLogo,
					show: showSaludiaNetwork,
					type: ServicesTypesEnum.OTHERS,
					handleClick: () =>
						openSaludiaDrawer('card-code', {returnToOptionsDrawer: false, origin: 'services'}),
				},
			],
		},
		{
			category: CategoriesEnum.NOTREDAME_NETWORK,
			list: [
				{
					title: ServicesTitlesEnum.APPOINTMENT_BOOKING,
					icon: 'stethoscope',
					route: HealthBookingEnum.HEALTH_BOOKING_FLOW,
					show: showBookHealth,
					type: ServicesTypesEnum.HEALTH,
					dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_CONSULTA,
				},
				{
					title: ServicesTitlesEnum.EXAM_BOOKING,
					icon: 'needle',
					route: ExamBookingEnum.EXAM_BOOKING_FLOW,
					show: showBookExams,
					type: ServicesTypesEnum.EXAMS,
					dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_EXAME,
				},
				{
					title: ServicesTitlesEnum.ADVANCE_DIAGNOSTICS,
					icon: 'syringe-default',
					show: showBookExams && isAdvanceDiagnosticsExamsJourneyEnabled,
					type: ServicesTypesEnum.EXAMS,
					handleClick: startAdvanceDiagnosticsBooking,
				},
				{
					title: ServicesTitlesEnum.ODONTO_BOOKING,
					icon: 'tooth-outline',
					route: OdontoBookingEnum.ODONTO_BOOKING_FLOW,
					show: showBookOdonto,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTypesEnum.ODONTO,
				},
				{
					title: ServicesTitlesEnum.NETWORK_PROVIDER_HEALTH,
					icon: 'stethoscope',
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_GUIA_MEDICO,
					handleClick: () => {
						setPpoNetworkType(ppoNetworkTypeEnum.SELF_NETWORK);
						navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);
					},
				},
				{
					title: ServicesTitlesEnum.MY_REFERRALS,
					icon: 'assignment-outline',
					route: MyReferralsEnum.MY_REFERRALS,
					show: showMyReferrals,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: MyReferralsTaggingEnum.CLICK_ACESSAR_MEUS_ENCAMINHAMENTOS_MENU,
				},
				{
					title: ServicesTitlesEnum.MY_HOSPITALIZATION,
					icon: 'local-hospital-filled',
					route: MyHospitalizationFlowEnum.HOME,
					show: showHospitalizationService,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MINHA_INTERNACAO,
				},
				{
					title: ServicesTitlesEnum.EXAM_RESULTS,
					icon: 'clipboard-pulse-outline',
					show: showExamsResults,
					featureFlag: EXAMS_FEATURE_BY_HOST[dataHostNameState.product],
					route: ExamResultsEnum.EXAM_RESULTS,
					endpoint: makeGetExamResultsUrl,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_RESULTADO_DE_EXAMES,
				},
				{
					title: ServicesTitlesEnum.MY_APPOINTMENTS,
					icon: 'calendar-focused-filled',
					route: isValid
						? MyAppointmentsEnum.MY_APPOINTMENTS
						: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY,
					show: showMyAppointments && !isEarlyAccess,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: AppointmentBookingEnum.CLICK_VER_MEUS_AGENDAMENTOS,
				},
			],
		},
		{
			category: CategoriesEnum.TELEHEALTH,
			list: [
				{
					title: ServicesTitlesEnum.EMERGENCY_TELEHEALTH,
					icon: 'video-default',
					show: showEmergencyTelehealth,

					type: ServicesTypesEnum.EMERGENCY_TELEHEALTH,
					dataTagId: AppointmentBookingEnum.CLICK_PRONTO_ATENDIMENTO_DIGITAL,
					...emergencyTelehealthService,
				},
				{
					title: ServicesTitlesEnum.TELEHEALTH_BOOKING,
					icon: 'calendar-add-filled',
					route: TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW,
					show: showBookTelehealth,
					type: ServicesTypesEnum.TELEHEALTH,
					dataTagId: AppointmentBookingEnum.CLICK_AGENDAR_TELECONSULTA,
				},
			],
		},
		{
			category: CategoriesEnum.FINANCIAL_AND_DOCS,
			list: [
				{
					title: ServicesTitlesEnum.INVOICES,
					icon: 'receipt-long-filled',
					route: MyPaymentsEnum.OPEN_BILLS,
					show: isIndividualPlan && !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_BOLETOS,
				},
				{
					title: isHolder ? ServicesTitlesEnum.REFUND : ServicesTitlesEnum.CONSULT_REFUND,
					icon: 'currency-exchange-filled',
					route: RefundEnum.REFUND,
					show: showRefund,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: isHolder
						? RefundTaggingEnum.CLICK_ACESSAR_REEMBOLSO_SAUDE
						: undefined,
				},
				{
					title: ServicesTitlesEnum.REFUND_ODONTO,
					icon: 'odonto-exchange-filled',
					externalLink: NOTREDAME_REFUND_ODONTO_URL,
					show: showRefundOdonto,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_REEMBOLSO_ODONTO,
				},
				{
					title: ServicesTitlesEnum.COPAYMENT_STATEMENT,
					icon: 'text-box-outline',
					route: MyPaymentsEnum.COPARTICIPATION,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_EXTRATO_DE_CO_PARTICIPACAO,
				},
				{
					title: ServicesTitlesEnum.INCOME_TAX_STATEMENT,
					icon: 'lion-filled',
					route: MyPaymentsEnum.INCOME_TAX,
					show: showIrDemonstrative(),
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_DEMOSTRATIVO_IR,
				},
				{
					title: ExternalServicesTitlesEnum.INVOICES,
					icon: 'barcode-default',
					externalLink: getInvoicesNFLinkByProduct(dataHostNameState.product),
					show: showInvoicesOption,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_NOTAS_FISCAIS,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_DUE_DATE,
					icon: 'event-repeat',
					externalLink: isAdmChangeDueDateEnabled
						? undefined
						: NOTREDAME_SERVICE_CENTER_URL,
					route: isAdmChangeDueDateEnabled
						? ChangeDueDateEnum.CHANGE_DUE_DATE
						: undefined,
					show: showServiceCenterLinks && isHolder && !odontoOnly,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_ALTERAR_DATA_DE_VENCIMENTO,
				},
				{
					title: ExternalServicesTitlesEnum.DEBT_SETTLEMENT_STATEMENT,
					icon: 'gavel-filled',
					endpoint: makeGetDebtSettlementStatementUrl,
					show: true,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_DEMONSTRATIVO_DE_QUITACAO_DE_DEBITOS,
				},
				{
					title: ServicesCenterTitlesEnum.DECLARATIONS_AND_DOCUMENTS,
					icon: 'pending-action',
					externalLink: NOTREDAME_SERVICE_CENTER_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DECLARACOES_DOCUMENTOS,
				},
				{
					title: ExternalServicesTitlesEnum.BENEFICIARY_DOCUMENT,
					icon: 'document-scanner',
					endpoint: makeGetBeneficiaryDocumentUrl,
					show: isNotredameSaude,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_DOCS_E_FORMS,
				},
				{
					title: ExternalServicesTitlesEnum.FINANCIAL_SUPPORT,
					icon: 'chat-filled',
					externalLink: financialSupportWhatsappLink,
					show: !(businessPlan && isHidePaymentSupportEnabled),
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ATENDIMENTO_FINANCEIRO_WHATSAPP,
				},
			],
		},
		{
			category: CategoriesEnum.MANAGEMENT,
			list: [
				{
					title: ServicesTitlesEnum.TOKEN,
					icon: 'key-filled',
					route: TokenEnum.TOKEN,
					show: isNotredameSaude,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_TOKEN,
				},
				{
					title: ServicesTitlesEnum.AUTHORIZATIONS_HEALTH,
					icon: 'document-heart-outlined',
					show: showHealthAuthorizations,
					route: AuthorizationsEnum.AUTHORIZATIONS,
					type: ServicesTypesEnum.AUTHORIZATIONS,
					dataTagId: AutoSolEnum.CLICK_ACESSAR_AUTOSOL_SAUDE,
				},
				{
					title: ServicesTitlesEnum.AUTHORIZATIONS_ODONTO,
					icon: 'document-tooth-outlined',
					show: showOdontoAuthorizations,
					route: AuthorizationsEnum.AUTHORIZATIONS_ODONTO,
					type: ServicesTypesEnum.AUTHORIZATIONS,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN,
					icon: 'books-filled',
					externalLink: NOTREDAME_SERVICE_CENTER_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDANCA_DE_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_ADAPTATION,
					icon: 'source-filled',
					externalLink: NOTREDAME_PORTABILITY_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADAPTACAO_DE_CONTRATOS,
				},
				{
					title: ServicesCenterTitlesEnum.REGISTRATION_CHANGE,
					icon: 'user-check',
					externalLink: NOTREDAME_PORTABILITY_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ALTERACAO_CADASTRAL,
				},
				{
					title: ServicesCenterTitlesEnum.DEPENDENT_INCLUSION,
					icon: 'person-add',
					externalLink: NOTREDAME_SERVICE_CENTER_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_INCLUSAO_DE_DEPENDENTES,
				},
				{
					title: ServicesCenterTitlesEnum.CONTRACT_REACTIVATION,
					icon: 'task-filled',
					externalLink: NOTREDAME_SERVICE_CENTER_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId:
						ExternalServicesTaggingEnum.CLICK_REATIVACAO_DE_CONTRATO_CANCELADO_POR_INADIMPLENCIA,
				},
				{
					title: ServicesCenterTitlesEnum.CHANGE_PLAN_HOLDER,
					icon: 'join-left-filled',
					externalLink: NOTREDAME_PORTABILITY_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_MUDAR_TITULAR_DO_PLANO,
				},
				{
					title: ServicesCenterTitlesEnum.PAD_ADHESION,
					icon: 'manage-account-filled',
					externalLink: NOTREDAME_PORTABILITY_URL,
					show: showServiceCenterLinks,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_ADESAO_AO_PAD,
				},
			],
		},
		{
			category: CategoriesEnum.PORTABILITY_AND_PERMANENCE,
			list: [
				{
					title: ExternalServicesTitlesEnum.PIN_SS,
					icon: 'keyboard-filled',
					endpoint: makeGetPinSsUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_PIN_SS,
				},
				{
					title: ExternalServicesTitlesEnum.STAY_LETTER,
					icon: 'mark-as-read',
					endpoint: makeGetStayLetterUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					isBlob: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_PERMANENCIA,
				},
				{
					title: ExternalServicesTitlesEnum.PORTABILITY_CARD,
					icon: 'mark-as-unread',
					endpoint: makeGetPortabilityCardUrl,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CARTA_DE_PORTABILIDADE,
				},
			],
		},
		{
			category: CategoriesEnum.PERKS,
			list: [
				{
					title: ServicesTitlesEnum.ADDITIONAL_COVERAGE,
					icon: 'circular-star',
					endpoint: makeGetAdditionalCoverage,
					show: showBenefitsForGndiSaoPaulo,
					type: ServicesTypesEnum.OTHERS,
					openInIframe: true,
					dataTagId: ExternalServicesTaggingEnum.CLICK_COBERTURAS_ADICIONAIS,
				},
				{
					title: ServicesTitlesEnum.PERKS_CLUB,
					icon: 'basket',
					endpoint: makeGetPerksClubLink,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CLUBE_DE_VANTAGENS,
				},
				{
					title: ServicesTitlesEnum.RECOMMENDATION,
					icon: 'add-reaction-filled',
					endpoint: makeGetWhoIndicatesLink,
					disableCache: true,
					show: !isResponsibleWithoutBenefits && !isHideRecommendationEnabled,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_QUEM_INDICA,
				},
				{
					title: ServicesTitlesEnum.NOTREDAME_CUIDAE,
					icon: 'percent-discount',
					route: PerksEnum.PERKS,
					show: isLifePlaceEnabled,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_CUIDAE,
				},
			],
		},

		{
			category: CategoriesEnum.SUPPORT,
			list: [
				{
					title: ServicesTitlesEnum.CONSULT_PROTOCOLS,
					icon: 'content-paste-search-filled',
					route: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_CONSULTA_DE_PROTOCOLOS,
				},
				{
					title: ServicesTitlesEnum.SERVICE_CHANNELS,
					icon: 'help-filled',
					route: HelpCenterEnum.HELP,
					show: !isResponsibleWithoutBenefits,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ServicesTaggingEnum.CLICK_PRECISA_DE_AJUDA,
					keywords: ['ajuda'],
				},
				{
					title: ExternalServicesTitlesEnum.SUPPORT,
					icon: 'verified-user',
					endpoint: makeGetHelpDeskSacLinkPage,
					show: !isResponsibleWithoutBenefits && isShowOmbudsmanInChannelsEnabled,
					type: ServicesTypesEnum.OTHERS,
					dataTagId: ExternalServicesTaggingEnum.CLICK_OUVIDORIA,
				},
				{
					title: ServicesTitlesEnum.CONCIERGE,
					icon: 'bell',
					show: showConciergeButton,
					type: ServicesTypesEnum.OTHERS,
					handleClick: () => onOpenConciergeDrawer(),
					dataTagId: ServicesTaggingEnum.CLICK_CONCIERGE,
				},
			],
		},
	];

	const FILTERED_LIST = NOTREDAME_SERVICES_LIST.map(category => {
		const isSectionMatch = findItemByQuery({
			item: category.category,
			query,
		});

		const shouldIncludeItem = (item: Service) =>
			item?.show &&
			(isSectionMatch ||
				findItemByQuery({
					item: item?.title,
					query,
					keywords: item?.keywords,
				}));

		return {
			...category,
			list: category.list.filter(shouldIncludeItem),
		};
	});

	const categorizedServices = FILTERED_LIST.reduce(
		(acc, serviceCategory) => {
			acc[serviceCategory.category] = serviceCategory;
			return acc;
		},
		{} as Record<CategoriesEnum, ServiceList>,
	);

	const healthAppointmentsServices = categorizedServices[CategoriesEnum.NETWORK];
	const notredameHealthAppointmentsServices =
		categorizedServices[CategoriesEnum.NOTREDAME_NETWORK];
	const telehealthServices = categorizedServices[CategoriesEnum.TELEHEALTH];
	const financialAndDocsServices = categorizedServices[CategoriesEnum.FINANCIAL_AND_DOCS];
	const perksServices = categorizedServices[CategoriesEnum.PERKS];
	const portabilityAndPermanenceServices =
		categorizedServices[CategoriesEnum.PORTABILITY_AND_PERMANENCE];
	const supportServices = categorizedServices[CategoriesEnum.SUPPORT];
	const managementServices = categorizedServices[CategoriesEnum.MANAGEMENT];

	const isServiceListEmpty = FILTERED_LIST.every(item => item?.list?.length === 0);

	return {
		healthAppointmentsServices,
		notredameHealthAppointmentsServices,
		telehealthServices,
		financialAndDocsServices,
		perksServices,
		portabilityAndPermanenceServices,
		supportServices,
		managementServices,
		isServiceListEmpty,
	};
}
